import { SubtenantDto } from '@lmt/shared-types';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { MultiSelectDropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/MultiSelectDropDownInput';
import { ScriptInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/ScriptInput';
import {
  OptionButton,
  TogglableOptions,
} from '@lmt/unicorn-shared/app/components/patients/list/components/OptionButton';
import { useSubtenants } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Field } from '../../../common/Field';
import {
  PatientListFilterComponentProps,
  useFilteredPatientStatesOptions,
  usePatientStatusesOptions,
  useSexTypeOptions,
} from '../common/types';
import { DateRangeConfiguration } from './DateRangeConfiguration';

import styles from './PatientFilter.module.scss';
import { TagsFilter } from './TagsFilter';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useEventCallback } from '@mui/material';

interface Props {
  onConfigurationReset: () => void;
  sqlError?: string;
}

const togglableOptions = [
  'hasFilterBySubtenants',
  'hasFilterByTags',
  'hasFilterBySexTypes',
  'hasFilterByStatuses',
  'hasFilterByStates',
  'hasFilterByLastActivity',
  'hasFilterByLastVisit',
  'hasFilterBySqlFilter',
] as TogglableOptions[];

const subtenantPostfixRenderer = (subtenant: SubtenantDto) => (
  <span className={styles.code}>{subtenant?.code}</span>
);

export const PatientFilter: FC<Props & PatientListFilterComponentProps> = (
  props,
) => {
  const i18next = useScopedTranslation('patients.list.filter');
  const { values, onChange, hasUserChanges, sqlError, onConfigurationReset } =
    props;
  const claims = useClaims();

  const {
    isFilterDisplayed,
    hasFilterBySubtenants,
    hasFilterByTags,
    hasFilterBySexTypes,
    hasFilterByStatuses,
    hasFilterByStates,
    hasFilterByLastActivity,
    hasFilterByLastVisit,
    hasFilterBySqlFilter,
  } = values;

  const availableSubtenants: SubtenantDto[] = useSubtenants();
  const sexTypeOptions = useSexTypeOptions();
  const patientStatesOptions = useFilteredPatientStatesOptions();
  const patientStatusesOptions = usePatientStatusesOptions();

  const [sqlFilterValue, setSqlFilterValue] = useState(values.sqlFilter);

  const [sqlFilterValueDebounced] = useDebounce(sqlFilterValue, 1000, {
    trailing: true,
  });

  const onLastVisitChange: React.ComponentProps<
    typeof DateRangeConfiguration
  >['onChange'] = useEventCallback((value) =>
    onChange({
      lastVisit: value,
    }),
  );

  const onLastActivityChange: React.ComponentProps<
    typeof DateRangeConfiguration
  >['onChange'] = useEventCallback((value) =>
    onChange({
      lastActivity: value,
    }),
  );

  const [initialSqlFilterValue, setInitialSqlFilterValue] =
    useState(sqlFilterValue);
  useEffect(() => {
    if (
      typeof sqlFilterValueDebounced === 'string' &&
      // This is to prevent calling `onChange` right after filters are shown.
      // Use case: switch to 2nd page, close/open filters. The page should stay the same
      initialSqlFilterValue !== sqlFilterValueDebounced
    ) {
      onChange({ sqlFilter: sqlFilterValueDebounced });
      setInitialSqlFilterValue(null);
    }
  }, [sqlFilterValueDebounced]);

  useEffect(() => setSqlFilterValue(values.sqlFilter), [values.sqlFilter]);

  const canFilterByStates =
    claims.permissions.patientDeleteAndRestore ||
    claims.permissions.patientAnonymizeAndDeletePermanently;
  const filterOptions = canFilterByStates
    ? togglableOptions
    : togglableOptions.filter((x) => x !== 'hasFilterByStates');

  return (
    <div className={styles.blockContainer} data-test-id={'patients-filter'}>
      <div className={styles.headerContainer}>
        <p className={styles.header}>{i18next.t('header').toUpperCase()}</p>
        <OptionButton
          options={filterOptions}
          values={values}
          onChange={onChange}
        />
      </div>
      {hasFilterBySubtenants && (
        <Field title={i18next.t('fields.subtenants')}>
          <MultiSelectDropDownInput
            options={availableSubtenants}
            useVirtualization={false}
            idFunction={'id'}
            getOptionLabel={'name'}
            placeholder={i18next.t('control.select_subtenants')}
            allSelectedLabel={i18next.t('control.all_subtenants')}
            onValueChanged={(value) =>
              onChange({
                subtenants: value.map((x) => x.id),
              })
            }
            values={values.subtenants}
            postfixRenderer={subtenantPostfixRenderer}
            headerTitle={i18next.t('fields.subtenants')}
            hasSearchFilter={true}
          />
        </Field>
      )}
      {hasFilterByTags && (
        <Field title={i18next.t('fields.tags')}>
          <TagsFilter
            selectedTagIds={values.tags}
            onChange={(value) => onChange({ tags: value })}
          />
        </Field>
      )}
      {hasFilterBySexTypes && (
        <Field title={i18next.t('fields.sex_types')}>
          <MultiSelectDropDownInput
            options={sexTypeOptions}
            idFunction={'value'}
            getOptionLabel={'label'}
            placeholder={i18next.t('control.select_sex_types')}
            allSelectedLabel={i18next.t('control.all_sex_types')}
            values={values.sexTypes}
            onValueChanged={(value) => {
              onChange({
                sexTypes: value.map((x) => x.value),
              });
            }}
            headerTitle={i18next.t('fields.sex_types')}
          />
        </Field>
      )}
      {hasFilterByStatuses && (
        <Field title={i18next.t('fields.statuses')}>
          <MultiSelectDropDownInput
            options={patientStatusesOptions}
            idFunction={'value'}
            getOptionLabel={'label'}
            placeholder={i18next.t('control.select_statuses')}
            allSelectedLabel={i18next.t('control.all_statuses')}
            values={values.statuses}
            onValueChanged={(value) =>
              onChange({
                statuses: value.map((x) => x.value),
              })
            }
            headerTitle={i18next.t('fields.statuses')}
          />
        </Field>
      )}
      {canFilterByStates && hasFilterByStates && (
        <Field title={i18next.t('fields.states')}>
          <MultiSelectDropDownInput
            options={patientStatesOptions}
            idFunction={'value'}
            getOptionLabel={'label'}
            placeholder={i18next.t('control.select_states')}
            allSelectedLabel={i18next.t('control.all_states')}
            values={values.states}
            onValueChanged={(value) =>
              onChange({
                states: value.map((x) => x.value),
              })
            }
            headerTitle={i18next.t('fields.states')}
          />
        </Field>
      )}
      {hasFilterByLastActivity && (
        <Field title={i18next.t('fields.last_activity')}>
          <DateRangeConfiguration
            value={values.lastActivity ?? null}
            onChange={onLastActivityChange}
          />
        </Field>
      )}
      {hasFilterByLastVisit && (
        <Field title={i18next.t('fields.last_visit')}>
          <DateRangeConfiguration
            value={values.lastVisit ?? null}
            onChange={onLastVisitChange}
          />
        </Field>
      )}
      {hasFilterBySqlFilter && (
        <Field title={i18next.t('fields.sql_filter')}>
          <ScriptInput
            value={sqlFilterValue}
            onChange={setSqlFilterValue}
            predefinedText={i18next.t('control.sql_filter_preset')}
            error={!!sqlError}
            shortErrorText={
              !!sqlError ? i18next.t('control.sql_filter_error') : undefined
            }
            fullErrorText={sqlError}
          />
        </Field>
      )}
      <div className={styles.buttonContainer}>
        <Button
          color={ButtonColor.Primary}
          className={styles.button}
          title={i18next.t('control.reset_button')}
          onClick={onConfigurationReset}
          disabled={!hasUserChanges}
        />
      </div>
    </div>
  );
};
